/**
 * SceneBounds: wraps model content in @react-three/drei's <Bounds>
 * and manages auto-fit-to-bounds logic.
 *
 * Only the actual model meshes should be children of this component —
 * helpers (grid, axis, lights) are excluded so they don't affect the
 * bounding-box calculation.
 *
 * Auto-fit behaviour is controlled by the parent via `autoFit`:
 *   - `autoFit=true`  → schedule a fit once compilation finishes
 *   - `autoFit=false`  → no automatic fitting; user clicks the button
 *
 * `isInitialFit` controls whether the camera direction is reset (true)
 * or preserved (false) during the fit.
 */

import { type ReactNode, useEffect, useCallback, useRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { Bounds, useBounds } from '@react-three/drei';
import * as THREE from 'three';
import type { ColoredGeometry } from '@/components/viewer/types';

// ---------------------------------------------------------------------------
// Inner controller — must be a child of <Bounds> to access the bounds context
// ---------------------------------------------------------------------------

function BoundsController({
  geometry,
  object,
  coloredGeometries,
  isOrthographic,
  isCompiling,
  autoFit,
  isInitialFit,
  onReady,
  onFitComplete,
}: {
  geometry?: THREE.BufferGeometry;
  object?: THREE.Object3D;
  coloredGeometries: ColoredGeometry[];
  isOrthographic: boolean;
  isCompiling: boolean;
  autoFit: boolean;
  isInitialFit: boolean;
  onReady?: (fitFn: () => void) => void;
  onFitComplete?: () => void;
}) {
  const bounds = useBounds();
  const invalidate = useThree((s) => s.invalidate);

  // Track whether a fit is pending (deferred to useFrame to avoid cross-renderer
  // race with R3F's reconciler).
  const needsFit = useRef(false);

  // Whether the pending fit should reset direction (only for the initial fit).
  const pendingResetDirection = useRef(false);

  // Track previous isCompiling to detect the falling edge (true → false).
  const wasCompilingRef = useRef(false);

  // -------------------------------------------------------------------------
  // fitToBounds: the manual/button version — always preserves camera direction
  // -------------------------------------------------------------------------
  const fitToBounds = useCallback(() => {
    // Direction-preserving fit: by NOT calling .reset(), drei's fit()
    // moves the camera along the current look direction to a distance
    // that contains the bounding box. For perspective cameras this
    // preserves the viewing angle. For orthographic cameras, clip()+fit()
    // adjusts zoom without repositioning, so direction is preserved too.
    bounds.refresh().clip().fit();
  }, [bounds]);

  // -------------------------------------------------------------------------
  // internalFit: used by the auto-fit system; may reset direction on first fit
  // -------------------------------------------------------------------------
  const internalFit = useCallback(
    (resetDirection: boolean) => {
      if (resetDirection && isOrthographic) {
        // Initial fit for orthographic: reset to default view angle
        bounds.refresh().reset().clip().fit();
      } else {
        // All other cases: preserve direction
        bounds.refresh().clip().fit();
      }
    },
    [bounds, isOrthographic],
  );

  // Expose the direction-preserving fit function to parent (for the button).
  // fitToBoundsRef always holds the latest version so the stable wrapper
  // passed to onReady never goes stale.
  const fitToBoundsRef = useRef(fitToBounds);
  fitToBoundsRef.current = fitToBounds;

  const onReadyRegisteredRef = useRef(false);
  if (onReady && !onReadyRegisteredRef.current) {
    onReadyRegisteredRef.current = true;
    onReady(() => fitToBoundsRef.current());
  }

  // -------------------------------------------------------------------------
  // Auto-fit scheduling
  // -------------------------------------------------------------------------
  const hasGeometry =
    geometry !== undefined ||
    object !== undefined ||
    coloredGeometries.length > 0;

  // When autoFit is enabled and compilation finishes (isCompiling: true→false),
  // schedule a fit. Also schedule on first mount when geometry already exists
  // and we're not compiling.
  //
  // invalidate() is called to request a new frame from R3F's "demand"
  // frameloop so the useFrame callback that consumes needsFit actually runs.
  useEffect(() => {
    if (autoFit && hasGeometry && !isCompiling) {
      needsFit.current = true;
      pendingResetDirection.current = isInitialFit;
      invalidate();
    }
  }, [autoFit, hasGeometry, isCompiling, isInitialFit, invalidate]);

  // Detect the falling edge of isCompiling to trigger auto-fit for the
  // continuous auto-fit toggle (when the user toggles it on mid-session).
  useEffect(() => {
    if (wasCompilingRef.current && !isCompiling && autoFit && hasGeometry) {
      needsFit.current = true;
      pendingResetDirection.current = isInitialFit;
      invalidate();
    }
    wasCompilingRef.current = isCompiling;
  }, [isCompiling, autoFit, hasGeometry, isInitialFit, invalidate]);

  // Consume the pending fit inside R3F's render loop. By this point the
  // reconciler has committed the new meshes, so bounds.refresh() will
  // measure the correct geometry.
  useFrame(() => {
    if (needsFit.current && !isCompiling) {
      needsFit.current = false;
      const resetDir = pendingResetDirection.current;
      pendingResetDirection.current = false;
      internalFit(resetDir);
      onFitComplete?.();
    }
  });

  return null;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

interface SceneBoundsProps {
  children: ReactNode;
  geometry?: THREE.BufferGeometry;
  object?: THREE.Object3D;
  coloredGeometries: ColoredGeometry[];
  isOrthographic: boolean;
  isCompiling: boolean;
  /** Whether auto-fit is currently enabled (initial fit or user toggle) */
  autoFit: boolean;
  /** Whether this fit should reset camera direction (true only for the very first fit) */
  isInitialFit: boolean;
  onFitToBoundsReady?: (fitFn: () => void) => void;
  /** Called after an auto-fit executes (so parent can mark initial fit as done) */
  onFitComplete?: () => void;
}

export function SceneBounds({
  children,
  geometry,
  object,
  coloredGeometries,
  isOrthographic,
  isCompiling,
  autoFit,
  isInitialFit,
  onFitToBoundsReady,
  onFitComplete,
}: SceneBoundsProps) {
  // NOTE: `fit` and `observe` are intentionally omitted from <Bounds>.
  // Bounds' internal useLayoutEffect re-runs refresh+reset+fit whenever
  // `size` (canvas viewport) changes if those props are set. This caused
  // the camera to zoom out on panel resize or unrelated re-renders.
  // Fitting is handled exclusively by BoundsController, which only
  // re-fits when auto-fit is enabled or the user clicks the button.
  return (
    <Bounds clip margin={1.2} maxDuration={0.8}>
      <BoundsController
        geometry={geometry}
        object={object}
        coloredGeometries={coloredGeometries}
        isOrthographic={isOrthographic}
        isCompiling={isCompiling}
        autoFit={autoFit}
        isInitialFit={isInitialFit}
        onReady={onFitToBoundsReady}
        onFitComplete={onFitComplete}
      />
      {children}
    </Bounds>
  );
}
